import axios, { AxiosRequestConfig, AxiosInstance } from 'axios';
import { isNetworkError } from '../helpers/error';

export class RestError extends Error {
  public constructor(
    readonly category: ErrorCategory,
    readonly reason: string,
    readonly description: string
  ) {
    super(description);
  }
}
export enum ErrorCategory {
  Clients = 'Clients',
  Surveys = 'Surveys',
  Payments = 'Payments',
  Email = 'Email',
  Api = 'Api',
  Backend = 'Backend',
  Frontend = 'Frontend',
}

var _axiosClient: AxiosInstance | null = null;

export const getClient = (): AxiosInstance => {
  if (_axiosClient) {
    return _axiosClient;
  } else {
    throw new RestError(
      ErrorCategory.Frontend,
      'NoResponseReceived',
      "Something went wrong. The network client wasn't initialized before call"
    );
  }
};

export const init = (config?: AxiosRequestConfig) => {
  _axiosClient = axios.create(config);
};

export async function api<T>(
  url: string,
  params?: AxiosRequestConfig,
  axiosInstance?: AxiosInstance
): Promise<T> {
  const response = await apiResponse(url, params, axiosInstance);
  return response.data as Promise<T>;
}

export async function apiResponse(
  url: string,
  params?: AxiosRequestConfig,
  axiosInstance?: AxiosInstance
) {
  const client = axiosInstance || getClient();
  return client(url, params).catch(function(error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.data) {
        // eslint-disable-next-line no-throw-literal
        throw error.response.data as RestError;
      } else {
        const errorText = `Something went wrong. Network Error Code: ${error.response.status}`;
        throw new RestError(
          ErrorCategory.Frontend,
          'NoErrorResponseData',
          errorText
        );
      }
    } else if (isNetworkError(error)) {
      throw new RestError(
        ErrorCategory.Frontend,
        'NetworkUnreachable',
        'Network issue. Please check your internet connection.'
      );
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      throw new RestError(
        ErrorCategory.Frontend,
        'NoResponseReceived',
        'Something went wrong. The request was made but no response was received'
      );
    } else {
      // Something happened in setting up the request that triggered an Error
      throw new RestError(
        ErrorCategory.Frontend,
        'BadRequestSettings',
        `Something happened in setting up the request that triggered an Error. ${error.message}`
      );
    }
  });
}
